import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import { Divider, Radio } from "antd";
import dayjs, { Dayjs } from "dayjs";
import CommonStyles from "@components/CommonStyles";
import BookingSuccessModal from "@components/Booking/BookingSuccessModal";
import DoctorListModal from "@components/Booking/DoctorListModal";
import SymptomsField from "@components/Booking/SymptomsField";
import TimeSlotGrid from "@components/Booking/TimeSlotGrid";
import LinkedSitePicker from "@components/User/Package/PackageDetail/LinkedSitePicker";
import ServiceItemsPicker from "@components/User/Package/PackageDetail/ServiceItemsPicker";
import { baseRoutes } from "@base/routes/baseRoutes";
import useToggle from "@base/hooks/useToggle";
import useCommonSettings from "@base/hooks/useCommonSettings";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { prepareAttachmentsForUpload } from "@base/helpers/attachmentUpload";
import doctorService from "@base/modules/doctor/doctorService";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import IFilterDcotor from "@base/modules/doctor/interface/filterDoctor.interafce";
import appointmentService from "@base/modules/appointment/appointmentService";
import { ITimeTablePeriod } from "@base/modules/appointment/appointment.interface";
import { apiLangForRecord, bookableSlots, ISymptomSetting } from "@base/modules/appointment/bookingCommon";
import { describeBookingError } from "@base/modules/appointment/bookingErrors";
import symptonService from "@base/modules/symptom/symptonService";
import { IDeliveryAddress } from "@base/modules/customerDeliveryAddress/customerDeliveryAddress.interface";
import { IPackageFacility } from "@base/modules/doctor/doctorExtraInfo";
import patientSpecialPackageService from "@base/modules/patientSpecialPackage/patientSpecialPackageService";
import { ISpecialPackage } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import { chosenGroupExamItems, groupExamCatalogueView } from "@base/modules/patientSpecialPackage/groupExamSelection";
import {
  bookableSites,
  bookingWindow,
  extraInfoOf,
  localizedExtra,
  routePackage,
  toVnYmd,
} from "@base/modules/patientSpecialPackage/packageDetailRules";
import {
  buildCreateFromPackage,
  canSubmitPackageBooking,
  canUploadAttachments,
  canViewDoctorInfo,
  doctorFacilities,
  doctorForProvince,
  doctorPageSize,
  doctorsInGroup,
  effectiveServiceType,
  encodeCustomerAddress,
  encodeExtraFields,
  extraFieldConfigs,
  ExtraFieldValue,
  filterByPackageRules,
  firstMissingExtraField,
  hasProvinceMismatch,
  initialGroup,
  initialSymptoms,
  isAbicContext,
  isDefaultDoctor,
  isInsuranceContext,
  isOnlineContext,
  PackageBookingContext,
  serviceAddressMode,
  serviceCodeOf,
  serviceNameOf,
  symptomsRequired,
} from "@base/modules/patientSpecialPackage/packageBookingRules";
import { IPatientRecord, IPatientRecordData } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";
import { ISite } from "@base/modules/site/site.interface";
import AttachmentPicker from "./AttachmentPicker";
import CustomerAddressPicker from "./CustomerAddressPicker";
import DoctorPicker from "./DoctorPicker";
import ExtraFields from "./ExtraFields";
import FacilityPicker from "./FacilityPicker";

const SERVER_DATE = "YYYY-MM-DD";

interface ISuccess {
  key: string;
  title: string;
  description: string;
  detailLabel: string;
}

/**
 * Booking against a package already bought (`dat-lich-goi-web-spec.md` §12). Who, where and which
 * use were settled on the package screen; this form picks the day, the doctor and the time, plus
 * whatever the package's configuration asks for.
 */
export default function PackageBooking() {
  //! state
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { key } = useParams();
  const [searchParams] = useSearchParams();
  const detailCode = searchParams.get("detail_code") ?? undefined;
  const detailId = searchParams.get("detail_id") ?? undefined;
  const implementId = searchParams.get("implement_id") ?? undefined;
  const patientRecordCode = searchParams.get("patient_record_code") ?? undefined;
  const siteCode = searchParams.get("site_code") ?? undefined;
  const uiLang = i18n.resolvedLanguage;
  const { hotline } = useCommonSettings();

  const { open: openGroupDialog, shouldRender: shouldRenderGroupDialog, toggle: toggleGroupDialog } = useToggle();
  const { open: openMismatch, shouldRender: shouldRenderMismatch, toggle: toggleMismatch } = useToggle();
  const { open: openLeave, shouldRender: shouldRenderLeave, toggle: toggleLeave } = useToggle();
  const { open: openSites, shouldRender: shouldRenderSites, toggle: toggleSites } = useToggle();
  const { open: openItems, shouldRender: shouldRenderItems, toggle: toggleItems } = useToggle();
  const { open: openAllDoctors, shouldRender: shouldRenderAllDoctors, toggle: toggleAllDoctors } = useToggle();
  const { open: openRetry, shouldRender: shouldRenderRetry, toggle: toggleRetry } = useToggle();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [specialPackage, setSpecialPackage] = useState<ISpecialPackage>();
  const [record, setRecord] = useState<IPatientRecord>();
  const [site, setSite] = useState<ISite>();
  const [date, setDate] = useState<string>();
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState<boolean>(false);
  const [groupCode, setGroupCode] = useState<string>();
  const [dialogGroupCode, setDialogGroupCode] = useState<string>();
  const [doctorCode, setDoctorCode] = useState<string>();
  const [slots, setSlots] = useState<ITimeTablePeriod[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [slot, setSlot] = useState<ITimeTablePeriod>();
  const [customerAddress, setCustomerAddress] = useState<IDeliveryAddress>();
  const [facility, setFacility] = useState<IPackageFacility>();
  const [extraValues, setExtraValues] = useState<Record<string, ExtraFieldValue>>({});
  const [symptoms, setSymptoms] = useState<string>("");
  const [suggestions, setSuggestions] = useState<ISymptomSetting[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<ISuccess>();
  const [pendingUploadKey, setPendingUploadKey] = useState<string>();
  const [autoOpenPicker, setAutoOpenPicker] = useState<"address" | "facility">();
  // The group question is asked once per visit; a new day does not ask it again.
  const askedGroupRef = useRef<boolean>(false);
  // Set when a group was just chosen: its picker opens once the doctor behind it is known.
  const openPickerNextRef = useRef<boolean>(false);

  //! context
  const context: PackageBookingContext | undefined = useMemo(() => {
    if (!specialPackage || !record || !site) {
      return undefined;
    }

    const details = specialPackage.patient_special_package_details ?? [];
    const service =
      details.find((item) => detailId && String(item.id) === detailId) ??
      details.find((item) => detailCode && item.code === detailCode) ??
      details[0];

    if (!service) {
      return undefined;
    }

    const visit = (service.patient_special_package_detail_implements ?? []).find((item) => String(item.id) === implementId) ?? null;
    const route = routePackage(specialPackage);

    return {
      route,
      pkg: specialPackage,
      service,
      visit,
      record,
      site,
      branch:
        route === "LEGACY" && specialPackage.site_branch_code
          ? { code: specialPackage.site_branch_code, name: specialPackage.site_branch_name }
          : null,
      window: bookingWindow(specialPackage, visit),
      groupExamItems: route === "GROUP_EXAM" ? chosenGroupExamItems(specialPackage) : [],
    };
  }, [specialPackage, record, site, detailId, detailCode, implementId]);

  const lang = apiLangForRecord(record, uiLang);
  const extraInfo = useMemo(() => extraInfoOf(specialPackage), [specialPackage]);
  const extraConfigs = useMemo(() => (context ? extraFieldConfigs(context.pkg, context.service) : []), [context]);
  const isAbic = !!context && isAbicContext(context);
  const sites = useMemo(() => (specialPackage ? bookableSites(specialPackage) : []), [specialPackage]);

  const { doctors: eligibleDoctors, groups } = useMemo(
    () => (context ? filterByPackageRules(doctors, context, uiLang) : { doctors: [] as IDoctor[], groups: [] }),
    [doctors, context, uiLang],
  );
  const mustChooseGroup = isAbic && groups.length >= 2 && !groupCode;
  const shownDoctors = useMemo(
    () => (mustChooseGroup ? [] : doctorsInGroup(eligibleDoctors, groupCode, uiLang)),
    [mustChooseGroup, eligibleDoctors, groupCode, uiLang],
  );
  const selectedDoctor = shownDoctors.find((doctor) => doctor.doctor_code === doctorCode);
  const addressMode = context ? serviceAddressMode(context, selectedDoctor, groupCode, groups.length > 0) : "NONE";

  //! function
  const alertError = useCallback(
    (description: string) => dispatch(setAlertMessage({ type: "error", message: t("Notification"), description })),
    [dispatch, t],
  );

  useEffect(() => {
    if (!key) {
      return;
    }

    (async () => {
      try {
        const [packageResp, recordsResp] = await Promise.all([
          patientSpecialPackageService.getDetail(key),
          profileService.getByUserKey().catch(() => ({ data: [] as IPatientRecordData[] })),
        ]);
        const pkg = packageResp.data;
        const route = routePackage(pkg);
        const found = (recordsResp.data ?? []).find((item) => item.patient_record?.code === patientRecordCode)?.patient_record;
        const chosenSite = route === "LEGACY" ? pkg.site : (bookableSites(pkg).find((item) => item.code === siteCode) ?? bookableSites(pkg)[0]);

        setSpecialPackage(pkg);
        setRecord(found ?? pkg.patient_record);
        setSite(chosenSite);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    })();
  }, [key, patientRecordCode, siteCode]);

  // The first day offered is the earliest the package allows.
  useEffect(() => {
    if (context && !date) {
      setDate(toVnYmd(context.window.min));
      setSymptoms(initialSymptoms(context));
    }
  }, [context, date]);

  useEffect(() => {
    symptonService
      .getListSymptom({ lang: uiLang ?? "vi" })
      .then((resp) => setSuggestions(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => setSuggestions([]));
  }, [uiLang]);

  const doctorQuery: Omit<IFilterDcotor, "page" | "size"> | undefined = useMemo(() => {
    if (!context || !date) {
      return undefined;
    }

    const serviceCode = serviceCodeOf(context);

    return {
      doctor_name: "",
      lang,
      appt_date: date,
      site_code: context.site.code,
      online: isOnlineContext(context),
      specialization_code: "",
      ...(serviceCode ? { service_code: serviceCode } : {}),
      ...(context.branch?.code ? { site_branch_code: context.branch.code } : {}),
    };
  }, [context, date, lang]);

  // Every new day or site starts the choices below it over.
  useEffect(() => {
    if (!doctorQuery || !context) {
      return;
    }

    let ignore = false;

    (async () => {
      try {
        setIsLoadingDoctors(true);
        setDoctorCode(undefined);
        setSlot(undefined);
        setFacility(undefined);
        const resp = await doctorService.getList({ ...doctorQuery, page: 0, size: doctorPageSize(context) });

        if (!ignore) {
          setDoctors(resp.data.content_page ?? []);
          setTotalPage(resp.data.total_page ?? 0);
        }
      } catch (error) {
        if (!ignore) {
          setDoctors([]);
          setTotalPage(0);
        }
      } finally {
        if (!ignore) {
          setIsLoadingDoctors(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [doctorQuery, context]);

  // The group: kept if still offered, else the first - but ABIC with several asks the patient once.
  useEffect(() => {
    if (isLoadingDoctors) {
      return;
    }

    const next = initialGroup(groups, groupCode, isAbic);

    if (next !== groupCode) {
      setGroupCode(next);
    }

    if (isAbic && groups.length >= 2 && !next && !askedGroupRef.current) {
      askedGroupRef.current = true;
      setDialogGroupCode(groups[0].code);
      toggleGroupDialog();
      return;
    }

    // A single group goes straight on to where the service happens.
    if (isAbic && groups.length === 1 && !askedGroupRef.current) {
      askedGroupRef.current = true;
      openPickerNextRef.current = true;
    }
  }, [groups, groupCode, isAbic, isLoadingDoctors, toggleGroupDialog]);

  // The first doctor of the list is the default, and stays chosen while it is still listed.
  useEffect(() => {
    if (isLoadingDoctors) {
      return;
    }

    if (!shownDoctors.some((doctor) => doctor.doctor_code === doctorCode)) {
      setDoctorCode(shownDoctors[0]?.doctor_code);
      setSlot(undefined);
      setFacility(undefined);
    }
  }, [shownDoctors, doctorCode, isLoadingDoctors]);

  useEffect(() => {
    if (!openPickerNextRef.current || !doctorCode || addressMode === "NONE") {
      return;
    }

    openPickerNextRef.current = false;
    setAutoOpenPicker(addressMode === "HOME" ? "address" : "facility");
  }, [doctorCode, addressMode]);

  // Leaving home care drops the address waiting for it.
  useEffect(() => {
    if (addressMode !== "HOME") {
      setCustomerAddress(undefined);
    }
  }, [addressMode]);

  const loadSlots = useCallback(async () => {
    if (!doctorCode || !date || !context) {
      setSlots([]);
      return;
    }

    try {
      setIsLoadingSlots(true);
      const resp = await doctorService.getTimeTable(doctorCode, date);

      setSlots(bookableSlots(resp.data.time_table_period ?? [], date, Date.now(), context.window.max));
    } catch (error) {
      setSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [doctorCode, date, context]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  /**
   * Choosing a group (again) is what asks where the service happens: the address or clinic list opens
   * straight away. A new group starts on its first doctor; the same group keeps the one already chosen.
   */
  const onSelectGroup = (code: string) => {
    const keepsDoctor = code === groupCode && !!selectedDoctor;
    const doctor = keepsDoctor ? selectedDoctor : doctorsInGroup(eligibleDoctors, code, uiLang)[0];
    const mode = context ? serviceAddressMode(context, doctor, code, true) : "NONE";

    openPickerNextRef.current = false;
    setGroupCode(code);

    if (!keepsDoctor) {
      setDoctorCode(doctor?.doctor_code);
      setSlot(undefined);
      setFacility(undefined);
    }

    setAutoOpenPicker(mode === "HOME" ? "address" : mode === "FACILITY" ? "facility" : undefined);
  };

  const onSelectDoctor = (doctor: IDoctor) => {
    if (doctor.doctor_code === doctorCode) {
      return;
    }

    setDoctorCode(doctor.doctor_code);
    setSlot(undefined);
    setFacility(undefined);
  };

  /** A home address picks the doctor covering its province, else the "other provinces" one. */
  const onSelectCustomerAddress = useCallback(
    (address: IDeliveryAddress) => {
      setCustomerAddress(address);
      const suggested = doctorForProvince(shownDoctors, address.customer_delivery_province_code);

      if (suggested && suggested.doctor_code !== doctorCode) {
        setDoctorCode(suggested.doctor_code);
        setSlot(undefined);
      }
    },
    [shownDoctors, doctorCode],
  );

  const onAutoOpened = useCallback(() => setAutoOpenPicker(undefined), []);

  const displayName = useCallback(
    (doctor: IDoctor) => (isDefaultDoctor(doctor) ? t("booking.defaultDoctor") : String(doctor.doctor_name ?? "")),
    [t],
  );
  const narrowDoctors = useCallback(
    (list: IDoctor[]) => (context ? doctorsInGroup(filterByPackageRules(list, context, uiLang).doctors, groupCode, uiLang) : list),
    [context, groupCode, uiLang],
  );

  const create = async () => {
    if (!context || !date || !slot || !selectedDoctor) {
      return;
    }

    if (!context.visit?.id) {
      alertError(t("packageBooking.tryAgainLater"));
      return;
    }

    const body = buildCreateFromPackage(context, {
      date,
      slot,
      doctor: selectedDoctor,
      symptoms,
      serviceType: effectiveServiceType(context, groupCode),
      customerAddressJSON: addressMode === "HOME" && customerAddress ? encodeCustomerAddress(customerAddress) : undefined,
      facilityAddress: addressMode === "FACILITY" ? facility?.address?.trim() : undefined,
      extraFieldsJSON: encodeExtraFields(extraConfigs, extraValues),
    });

    try {
      setIsSubmitting(true);
      const resp = await appointmentService.createFromPackage(body);
      const appointment = resp.data ?? {};

      if (isInsuranceContext(context) && attachments.length && canUploadAttachments(appointment)) {
        const uploaded = await uploadAttachments(String(appointment.key));

        if (!uploaded) {
          setPendingUploadKey(String(appointment.key));
          toggleRetry();
          return;
        }
      }

      showSuccess(String(appointment.key ?? ""));
    } catch (error) {
      onBookingError(error as AxiosError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadAttachments = async (appointmentKey: string) => {
    try {
      const prepared = await prepareAttachmentsForUpload(attachments);

      if (prepared.length) {
        await appointmentService.uploadAttachments(appointmentKey, prepared);
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const showSuccess = (appointmentKey: string) => {
    if (!context || !date) {
      return;
    }

    if (isInsuranceContext(context)) {
      setSuccess({
        key: appointmentKey,
        title: t("booking.success.title"),
        description: t("booking.success.insuranceDescription"),
        detailLabel: t("user.servicePackage.btnApptDetail"),
      });
      return;
    }

    const online = isOnlineContext(context);

    setSuccess({
      key: appointmentKey,
      title: online ? t("booking.success.onlineTitle") : t("booking.success.title"),
      description: online
        ? t("booking.success.onlineDescription")
        : t("booking.success.offlineDescription", { date: dayjs(date).format("DD-MM-YYYY"), site: context.site.name ?? "" }),
      detailLabel: t("booking.success.detail"),
    });
  };

  const onBookingError = (error: AxiosError) => {
    if (!error.response) {
      alertError(t("booking.noConnection"));
      return;
    }

    const result = describeBookingError(error.response.data, {
      translate: t,
      hotline,
      groupExam:
        context?.route === "GROUP_EXAM"
          ? {
              halfDayTemplate: localizedExtra(extraInfo, "noti_half_day_quota_reached", uiLang) ?? t("booking.groupExam.halfDayQuotaReached"),
              quotaPercent: typeof extraInfo.half_day_session_quota_check_from === "string" ? extraInfo.half_day_session_quota_check_from : undefined,
            }
          : undefined,
      periodStartTime: slot?.period_start_time,
      appointmentDate: date,
    });

    if (result.kind === "generic") {
      alertError(t("booking.genericError"));
      return;
    }

    alertError(result.text);

    if (result.reloadSlots) {
      setSlot(undefined);
      loadSlots();
    }
  };

  const onSubmit = () => {
    if (!context) {
      return;
    }

    setErrors({});

    if (mustChooseGroup) {
      alertError(t("booking.serviceGroupRequired"));
      return;
    }

    if ((addressMode === "HOME" && !customerAddress) || (addressMode === "FACILITY" && !facility)) {
      setErrors({ serviceAddress: t("booking.serviceAddressRequired") });
      alertError(t("booking.serviceAddressRequired"));
      return;
    }

    const missing = firstMissingExtraField(extraConfigs, extraValues, uiLang);

    if (missing) {
      const label = missing.part
        ? `${missing.label} [${t(missing.part === "PROVINCE" ? "packageBooking.province" : "packageBooking.ward")}]`
        : missing.label;

      setErrors({ [missing.field]: t("packageBooking.requiredNamedField", { label }) });
      return;
    }

    if (addressMode === "HOME" && hasProvinceMismatch(selectedDoctor, customerAddress)) {
      toggleMismatch();
      return;
    }

    create();
  };

  const backToPackage = () => navigate(baseRoutes.userPackageDetail.replace(":key", String(key)));

  //! render
  if (isLoading) {
    return <CommonStyles.LoadingUI />;
  }

  if (!context || !date) {
    return (
      <div className="p-5 flex flex-col items-center gap-4">
        <span className="text-common-subtext text-sm font-['Inter']">{t("packageBooking.tryAgainLater")}</span>
        <CommonStyles.ButtonUI onClick={backToPackage}>{t("booking.back")}</CommonStyles.ButtonUI>
      </div>
    );
  }

  const isInsurance = isInsuranceContext(context);
  const doctorLabel =
    localizedExtra(extraInfo, "label_select_doctor_list", uiLang) ?? localizedExtra(extraInfo, "change_label_select_doctor_list", uiLang);
  const emptyDoctorText = mustChooseGroup
    ? t("booking.serviceGroupPlaceholder")
    : (localizedExtra(extraInfo, "empty_doctor_list", uiLang) ?? t("booking.noDoctor"));
  const canSubmit = context.window.bookable && canSubmitPackageBooking(context, { date, slot, doctor: selectedDoctor, symptoms });
  const minDate = dayjs(toVnYmd(context.window.min));
  const maxDate = context.window.max ? dayjs(toVnYmd(context.window.max)) : undefined;
  const showSite = context.route === "LEGACY" || sites.length >= 2;

  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="text-common-primary text-base font-bold font-['Inter']">{t("booking.packageTitle")}</div>

      <Divider className="my-0" />

      {!context.window.bookable ? (
        <div className="w-full bg-common-red100 border border-common-red500 p-3 rounded-lg text-common-red500 text-sm font-['Inter']">
          {t("booking.expiredCannotBook")}
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("packageBooking.patientRecord")}</span>
        <div className="bg-common-background rounded-lg p-3 flex flex-col gap-0.5">
          <span className="text-common-maintext text-sm font-semibold font-['Inter']">{context.record.patient_name}</span>
          <span className="text-common-subtext text-xs font-['Inter']">
            {[
              context.record.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              context.record.patient_dob ? dayjs(context.record.patient_dob, SERVER_DATE).format("DD/MM/YYYY") : null,
              isInsurance ? context.record.contact_phone || context.record.patient_phone_number : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </div>
      </div>

      {showSite ? (
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("Appointment.facility")}</span>
            {context.route !== "LEGACY" ? (
              <button type="button" className="text-common-primary text-sm font-medium font-['Inter']" onClick={toggleSites}>
                {t("booking.change")}
              </button>
            ) : null}
          </div>
          <div className="bg-common-background rounded-lg p-3 flex flex-col gap-0.5">
            <span className="text-common-maintext text-sm font-semibold font-['Inter']">{context.branch?.name || context.site.name}</span>
            <span className="text-common-subtext text-xs font-['Inter']">{context.branch?.name ? context.site.name : context.site.address}</span>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("Appointment.service")}</span>
        <div className="bg-common-background rounded-lg p-3 flex justify-between items-center gap-2">
          <span className="text-common-maintext text-sm font-semibold font-['Inter'] line-clamp-2">{serviceNameOf(context)}</span>
          {context.route === "GROUP_EXAM" ? (
            <button type="button" className="text-common-primary text-sm font-medium font-['Inter'] shrink-0" onClick={toggleItems}>
              {t("booking.doctorDetail")}
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {t("packageBooking.serviceDate")} <span className="text-common-red500">*</span>
        </span>

        <CommonStyles.DatePickerUI
          variant="filled"
          format="DD/MM/YYYY"
          value={dayjs(date, SERVER_DATE)}
          allowClear={false}
          disabledDate={(current: Dayjs) => !!current && (current.isBefore(minDate, "day") || (!!maxDate && current.isAfter(maxDate, "day")))}
          onChange={(value: Dayjs) => {
            if (value) {
              setDate(value.format(SERVER_DATE));
              setSlot(undefined);
            }
          }}
        />
      </div>

      <DoctorPicker
        label={doctorLabel ?? t("booking.doctorTitle")}
        emptyText={emptyDoctorText}
        isLoading={isLoadingDoctors}
        groups={groups}
        doctors={shownDoctors}
        selectedGroupCode={groupCode}
        selectedDoctorCode={doctorCode}
        hint={addressMode === "HOME" ? customerAddress?.customer_delivery_province_name : undefined}
        displayName={displayName}
        canViewInfo={(doctor) => canViewDoctorInfo(context, doctor)}
        onSeeAll={shownDoctors.length && totalPage > 1 ? toggleAllDoctors : undefined}
        onSelectGroup={onSelectGroup}
        onSelectDoctor={onSelectDoctor}
      />

      {addressMode === "HOME" ? (
        <CustomerAddressPicker
          label={t("packageBooking.serviceAddress")}
          error={errors.serviceAddress}
          patientRecord={context.record}
          selected={customerAddress}
          autoOpen={autoOpenPicker === "address"}
          onAutoOpened={onAutoOpened}
          onSelect={onSelectCustomerAddress}
        />
      ) : null}

      {addressMode === "FACILITY" ? (
        <div className="flex flex-col gap-1">
          <FacilityPicker
            label={t("packageBooking.serviceAddress")}
            facilities={doctorFacilities(selectedDoctor)}
            selected={facility}
            hasError={!!errors.serviceAddress}
            autoOpen={autoOpenPicker === "facility"}
            onAutoOpened={onAutoOpened}
            onSelect={setFacility}
          />
          {errors.serviceAddress ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{errors.serviceAddress}</span> : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {t("Appointment.hour")} <span className="text-common-red500">*</span>
        </span>
        {doctorCode ? <TimeSlotGrid slots={slots} selected={slot} isLoading={isLoadingSlots} onChange={setSlot} /> : null}
      </div>

      <ExtraFields
        fields={extraConfigs}
        values={extraValues}
        errors={errors}
        onChange={(fieldName, value) => setExtraValues((prev) => ({ ...prev, [fieldName]: value }))}
      />

      <SymptomsField value={symptoms} required={symptomsRequired(context)} suggestions={suggestions} onChange={setSymptoms} />

      {isInsurance ? <AttachmentPicker files={attachments} onChange={setAttachments} /> : null}

      <div className="w-full flex justify-center items-center gap-4 mt-2">
        <CommonStyles.ButtonUI ghost type="primary" onClick={toggleLeave}>
          {t("booking.back")}
        </CommonStyles.ButtonUI>

        <CommonStyles.ButtonUI type="primary" loading={isSubmitting} disabled={!canSubmit} onClick={onSubmit}>
          {t("user.servicePackage.btnBooking")}
        </CommonStyles.ButtonUI>
      </div>

      {shouldRenderGroupDialog && (
        <CommonStyles.ModalConfirmationUI
          open={openGroupDialog}
          toggle={toggleGroupDialog}
          type="normal"
          title={doctorLabel ?? t("booking.serviceGroupTitle")}
          okText={t("booking.agree")}
          content={
            <div className="flex flex-col gap-3">
              <span className="text-common-subtext text-sm font-normal font-['Inter']">{t("packageBooking.pleaseChoose")}</span>

              <Radio.Group value={dialogGroupCode} onChange={(event) => setDialogGroupCode(event.target.value)} className="flex flex-col gap-2">
                {groups.map((group) => (
                  <Radio key={group.code} value={group.code}>
                    {group.name}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          }
          onConfirm={() => {
            toggleGroupDialog();

            if (dialogGroupCode) {
              onSelectGroup(dialogGroupCode);
            }
          }}
        />
      )}

      {shouldRenderMismatch && (
        <CommonStyles.ModalConfirmationUI
          open={openMismatch}
          toggle={toggleMismatch}
          title={t("Notification")}
          okText={t("packageBooking.continue")}
          content={t("packageBooking.provinceMismatch")}
          onConfirm={() => {
            toggleMismatch();
            create();
          }}
        />
      )}

      {shouldRenderLeave && (
        <CommonStyles.ModalConfirmationUI
          open={openLeave}
          toggle={toggleLeave}
          title={t("Notification")}
          okText={t("booking.agree")}
          content={t("booking.leaveConfirm")}
          onConfirm={() => {
            toggleLeave();
            backToPackage();
          }}
        />
      )}

      {shouldRenderSites && (
        <LinkedSitePicker
          open={openSites}
          toggle={toggleSites}
          sites={sites}
          selectedCode={site?.code}
          onPicked={(picked) => {
            toggleSites();
            setSite(picked);
          }}
        />
      )}

      {shouldRenderItems && specialPackage && (
        <ServiceItemsPicker
          open={openItems}
          toggle={toggleItems}
          mode="VIEW"
          title={serviceNameOf(context)}
          items={groupExamCatalogueView(specialPackage)}
          cap={0}
        />
      )}

      {shouldRenderAllDoctors && doctorQuery && (
        <DoctorListModal
          open={openAllDoctors}
          toggle={toggleAllDoctors}
          query={doctorQuery}
          narrow={narrowDoctors}
          selectedCode={doctorCode}
          displayName={displayName}
          onPick={(doctor) => {
            toggleAllDoctors();
            // A doctor from a later page joins the list so the form can show it as chosen.
            setDoctors((prev) => (prev.some((item) => item.doctor_code === doctor.doctor_code) ? prev : [...prev, doctor]));
            onSelectDoctor(doctor);
          }}
        />
      )}

      {shouldRenderRetry && (
        <CommonStyles.ModalConfirmationUI
          open={openRetry}
          toggle={() => {
            toggleRetry();
            showSuccess(String(pendingUploadKey ?? ""));
          }}
          title={t("Notification")}
          cancelText={t("Close")}
          okText={t("booking.retry")}
          content={t("booking.uploadFailed")}
          onConfirm={async () => {
            const uploaded = await uploadAttachments(String(pendingUploadKey));

            if (uploaded) {
              toggleRetry();
              showSuccess(String(pendingUploadKey));
            }
          }}
        />
      )}

      <BookingSuccessModal
        open={!!success}
        title={success?.title ?? ""}
        description={success?.description ?? ""}
        detailLabel={success?.detailLabel ?? ""}
        onHome={() => navigate(baseRoutes.home)}
        onDetail={() => navigate(success?.key ? baseRoutes.userAppointmentDetail.replace(":key", success.key) : baseRoutes.userAppointment)}
      />
    </div>
  );
}
